<script setup>
/**
 * Opening hours, a day at a time, with up to three times a day. Ease C3, spec §6 P-6.10.
 *
 * The value is the BFF's `businessHours` exactly: per day (`mon` to `sun`) a list of
 * `[start, end]` pairs in 24-hour `HH:MM`, or `null` for closed; the whole value `null` is
 * "no hours set". An end before its start runs past midnight (a café open 18:00 to 02:00).
 *
 * The rules are the server's (`hours.ts` in the BFF, `time-context.ts` in the sidecar, held
 * equal by `hours-parity.test.ts`): this control only offers what they accept (three times a
 * day at most) and shows the sentence the save comes back with, pinned on the field.
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import WasloInput from './WasloInput.vue';
import WasloSelect from './WasloSelect.vue';
import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  /** `{ mon: [[start, end]] | null, … }`, or `null` for no hours set. */
  modelValue: { type: Object, default: null },
  timezone: { type: String, default: 'Asia/Kuwait' },
  /** The save's sentence for this field, if it refused one. */
  message: { type: String, default: '' },
  /**
   * Field report G2.1: the sending hours reuse this for a week of their own and for a season's
   * week, under their own heading and with no time zone (the account has one, set above).
   */
  heading: { type: String, default: '' },
  description: { type: String, default: '' },
  withTimezone: { type: Boolean, default: true },
});

const emit = defineEmits(['update:modelValue', 'update:timezone']);

const { t } = useWasloI18n();

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const MAX_RANGES_PER_DAY = 3;
const DEFAULT_RANGE = ['09:00', '17:00'];

/** Every zone the browser knows, and always the one saved, even on an older browser. */
const zones = computed(() => {
  const known =
    typeof Intl.supportedValuesOf === 'function'
      ? Intl.supportedValuesOf('timeZone')
      : [];
  const list = known.includes(props.timezone)
    ? known
    : [props.timezone, ...known];
  return list.map(zone => ({ value: zone, label: zone }));
});

const week = computed(() =>
  Object.fromEntries(DAYS.map(day => [day, props.modelValue?.[day] ?? null]))
);

const write = (day, ranges) => {
  emit('update:modelValue', { ...week.value, [day]: ranges });
};

const setClosed = (day, closed) => {
  write(day, closed ? null : [[...DEFAULT_RANGE]]);
};

const setTime = (day, index, side, value) => {
  const ranges = (week.value[day] ?? []).map(range => [...range]);
  ranges[index][side] = value;
  write(day, ranges);
};

const addRange = day => {
  const ranges = week.value[day] ?? [];
  if (ranges.length >= MAX_RANGES_PER_DAY) return;
  write(day, [...ranges.map(range => [...range]), [...DEFAULT_RANGE]]);
};

const removeRange = (day, index) => {
  const ranges = (week.value[day] ?? []).filter((_, i) => i !== index);
  write(day, ranges.length ? ranges : null);
};
</script>

<template>
  <div class="flex flex-col gap-4" data-waslo-hours>
    <SettingsHeader
      :heading="heading || t('WASLO.HOURS.HEADING')"
      :description="description || t('WASLO.HOURS.DESCRIPTION')"
    />
    <WasloSelect
      v-if="withTimezone"
      :model-value="timezone"
      :label="t('WASLO.HOURS.TIMEZONE')"
      :options="zones"
      data-waslo-hours-timezone
      @update:model-value="value => emit('update:timezone', value)"
    />
    <ul class="flex flex-col gap-3">
      <li
        v-for="day in DAYS"
        :key="day"
        class="flex flex-col gap-2 sm:flex-row sm:items-start"
        :data-waslo-hours-day="day"
      >
        <span class="w-28 pt-2 text-sm font-medium text-n-slate-12 shrink-0">
          {{ t(`WASLO.HOURS.DAYS.${day.toUpperCase()}`) }}
        </span>
        <div class="flex flex-col flex-1 gap-2">
          <label class="inline-flex items-center gap-2 text-sm text-n-slate-11">
            <Checkbox
              :model-value="week[day] === null"
              data-waslo-hours-closed
              @update:model-value="value => setClosed(day, value)"
            />
            {{ t('WASLO.HOURS.CLOSED') }}
          </label>
          <template v-if="week[day] !== null">
            <div
              v-for="(range, index) in week[day]"
              :key="index"
              class="flex flex-wrap items-end gap-2"
              data-waslo-hours-range
            >
              <WasloInput
                type="time"
                :model-value="range[0]"
                :label="t('WASLO.HOURS.FROM')"
                @update:model-value="value => setTime(day, index, 0, value)"
              />
              <WasloInput
                type="time"
                :model-value="range[1]"
                :label="t('WASLO.HOURS.TO')"
                @update:model-value="value => setTime(day, index, 1, value)"
              />
              <!-- A close before the open runs past midnight (spec P-6.10). -->
              <span
                v-if="range[1] <= range[0]"
                class="pb-2 text-xs text-n-slate-11"
                data-waslo-hours-next-day
              >
                {{ t('WASLO.HOURS.NEXT_DAY') }}
              </span>
              <Button
                size="xs"
                variant="ghost"
                color="slate"
                :label="t('WASLO.HOURS.REMOVE_TIME')"
                data-waslo-hours-remove
                @click="removeRange(day, index)"
              />
            </div>
            <Button
              v-if="week[day].length < MAX_RANGES_PER_DAY"
              class="self-start"
              size="xs"
              variant="faded"
              color="slate"
              :label="t('WASLO.HOURS.ADD_TIME')"
              data-waslo-hours-add
              @click="addRange(day)"
            />
          </template>
        </div>
      </li>
    </ul>
    <p v-if="message" class="mb-0 text-sm text-n-ruby-9">{{ message }}</p>
  </div>
</template>
